/**
 * SegmentFieldConfigDialog - per-segment "Configure Fields" modal.
 *
 * Allows editing field labels, types and visibility modes
 * (Required / Optional / Collapsed / Excluded) for registration wizard segments.
 */

import { useState, useMemo, useEffect } from 'react'
import { toast } from 'sonner'
import { Pencil, Info } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/shared/components/ui/select'
import { SEGMENT_LABELS } from '@/admin/hooks/use-registration-settings'
import type { DataCaptureConfig, DataCaptureMode, DataCaptureFieldKey } from '@/admin/hooks/use-registration-settings'

// ---------------------------------------------------------------------------
// Field definitions
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'
interface FieldDef {
  key: DataCaptureFieldKey
  defaultLabel: string
  defaultFieldType: string
  /** If true, field_type cannot be changed (inherent to the field). */
  fieldTypeLocked: boolean
}

const CUSTOMER_FIELDS: FieldDef[] = [
  { key: 'consumer_email', defaultLabel: 'Email', defaultFieldType: 'EMAIL', fieldTypeLocked: true },
  { key: 'consumer_name', defaultLabel: 'Name', defaultFieldType: 'TEXT', fieldTypeLocked: true },
  { key: 'consumer_phone', defaultLabel: 'Phone Number', defaultFieldType: 'PHONE', fieldTypeLocked: true },
  { key: 'consumer_address', defaultLabel: 'Address', defaultFieldType: 'ADDRESS', fieldTypeLocked: true },
]

const TRANSACTION_FIELDS: FieldDef[] = [
  { key: 'transaction_date', defaultLabel: 'Purchase Date', defaultFieldType: 'DATE', fieldTypeLocked: true },
  { key: 'transaction_price', defaultLabel: 'Purchase Price', defaultFieldType: 'MONEY', fieldTypeLocked: true },
  { key: 'transaction_proof_of_purchase', defaultLabel: 'Proof of Purchase', defaultFieldType: 'DOCUMENT', fieldTypeLocked: false },
]

function fieldsForSegment(segment: string): FieldDef[] {
  if (segment === 'customer') return CUSTOMER_FIELDS
  if (segment === 'purchase') return TRANSACTION_FIELDS
  if (segment === 'purchase_customer') return [...CUSTOMER_FIELDS, ...TRANSACTION_FIELDS]
  return []
}

const FIELD_TYPE_OPTIONS = [
  'TEXT', 'NUMBER', 'DATE', 'DATE_TIME', 'BOOLEAN',
  'SELECT', 'EMAIL', 'PHONE', 'ADDRESS', 'MONEY',
  'DOCUMENT', 'FILE', 'IMAGE', 'LINK',
]

const FIELD_TYPE_LABEL: Record<string, string> = {
  TEXT: 'Text', NUMBER: 'Number', DATE: 'Date', DATE_TIME: 'Date/Time',
  BOOLEAN: 'Boolean', SELECT: 'Select', EMAIL: 'Email', PHONE: 'Phone',
  ADDRESS: 'Address', MONEY: 'Money', DOCUMENT: 'Document', FILE: 'File',
  IMAGE: 'Image', LINK: 'Link',
}

const DATA_CAPTURE_MODES: { value: DataCaptureMode; label: string }[] = [
  { value: 'REQUIRED', label: 'Required' },
  { value: 'OPTIONAL', label: 'Optional' },
  { value: 'COLLAPSED', label: 'Collapsed' },
  { value: 'EXCLUDED', label: 'Excluded' },
]

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

function FieldTypeBadge({ type }: { type: string }) {
  const label = FIELD_TYPE_LABEL[type] ?? type
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-2)]">
      {label}
    </span>
  )
}

function ModeBadge({ mode }: { mode: DataCaptureMode }) {
  return (
    <span className={`inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-2)] ${mode === 'EXCLUDED' ? 'line-through' : ''}`}>
      {mode.toLowerCase()}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Dialog component
// ---------------------------------------------------------------------------

export function SegmentFieldConfigDialog({
  segment,
  open,
  onOpenChange,
  config,
  onSave,
}: {
  segment: string
  open: boolean
  onOpenChange: (v: boolean) => void
  config?: DataCaptureConfig
  onSave: (cfg: Partial<DataCaptureConfig>) => Promise<void>
}) {
  const fields = useMemo(() => fieldsForSegment(segment), [segment])

  // Draft state for capture modes
  const [modes, setModes] = useState<Record<string, DataCaptureMode>>({})
  // Draft state for field overrides (label + field_type)
  const [overrides, setOverrides] = useState<Record<string, { label: string; field_type: string }>>({})
  const [saving, setSaving] = useState(false)
  // Which field is currently being edited (null = none)
  const [editingKey, setEditingKey] = useState<string | null>(null)

  // Sync when dialog opens
  useEffect(() => {
    if (!open) return
    const m: Record<string, DataCaptureMode> = {}
    const o: Record<string, { label: string; field_type: string }> = {}
    for (const f of fields) {
      m[f.key] = config?.[f.key] ?? 'OPTIONAL'
      const existing = config?.field_overrides?.[f.key]
      o[f.key] = {
        label: existing?.label ?? f.defaultLabel,
        field_type: existing?.field_type ?? f.defaultFieldType,
      }
    }
    setModes(m)
    setOverrides(o)
    setEditingKey(null)
  }, [open, config, fields])

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {}
      const fieldOverrides: Record<string, { label: string; field_type: string }> = {}
      for (const f of fields) {
        payload[f.key] = modes[f.key]
        fieldOverrides[f.key] = overrides[f.key]
      }
      payload.field_overrides = fieldOverrides
      await onSave(payload as Partial<DataCaptureConfig>)
      toast.success('Field configuration saved')
      onOpenChange(false)
    } catch (err) {
      toastApiError(err, 'Failed to save field configuration');
    } finally {
      setSaving(false)
    }
  }

  const segmentLabel = SEGMENT_LABELS[segment] || segment

  // Group fields by section when showing purchase_customer
  const customerFields = fields.filter((f) => f.key.startsWith('consumer_'))
  const transactionFields = fields.filter((f) => f.key.startsWith('transaction_'))
  const hasGroups = customerFields.length > 0 && transactionFields.length > 0

  function renderFieldRow(f: FieldDef) {
    const mode = modes[f.key] ?? 'OPTIONAL'
    const ov = overrides[f.key] ?? { label: f.defaultLabel, field_type: f.defaultFieldType }
    const isEditing = editingKey === f.key
    const isExcluded = mode === 'EXCLUDED'

    return (
      <div
        key={f.key}
        className={`transition-colors duration-100 ${
          isEditing ? 'bg-[var(--bg-subtle)]' : ''
        } ${isExcluded ? 'opacity-50' : ''}`}
      >
        {isEditing ? (
          <div className="flex flex-col gap-3 px-5 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">Label</Label>
                <Input
                  type="text"
                  value={ov.label}
                  onChange={(e) =>
                    setOverrides((prev) => ({
                      ...prev,
                      [f.key]: { ...prev[f.key], label: e.target.value },
                    }))
                  }
                  placeholder={f.defaultLabel}
                  autoFocus
                  className="h-8 text-[13px]"
                />
              </div>
              <div>
                <Label className="mb-1 block text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">Field Type</Label>
                {f.fieldTypeLocked ? (
                  <div className="flex h-8 items-center rounded-md border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3">
                    <span className="text-[13px] text-[var(--ink-3)]">
                      {FIELD_TYPE_LABEL[ov.field_type] ?? ov.field_type}
                    </span>
                  </div>
                ) : (
                  <Select
                    value={ov.field_type}
                    onValueChange={(v) =>
                      setOverrides((prev) => ({
                        ...prev,
                        [f.key]: { ...prev[f.key], field_type: v },
                      }))
                    }
                  >
                    <SelectTrigger className="h-8 w-full text-[13px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPE_OPTIONS.map((ft) => (
                        <SelectItem key={ft} value={ft}>
                          {FIELD_TYPE_LABEL[ft] ?? ft}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Select
                value={mode}
                onValueChange={(v) =>
                  setModes((prev) => ({ ...prev, [f.key]: v as DataCaptureMode }))
                }
              >
                <SelectTrigger className="h-7 w-[130px] text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATA_CAPTURE_MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="xs"
                type="button"
                onClick={() => setEditingKey(null)}
                className="cursor-pointer text-[11px] text-[var(--green)]"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between px-5 py-3">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setEditingKey(f.key)}
              className="group/edit flex h-auto cursor-pointer items-center gap-2 p-0"
              title="Click to edit"
            >
              <span className="text-[13px] text-[var(--ink)]">
                {ov.label}
              </span>
              <FieldTypeBadge type={ov.field_type} />
              <Pencil size={11} className="text-[var(--ink-3)] opacity-0 transition-opacity duration-100 group-hover/edit:opacity-100" />
            </Button>
            <ModeBadge mode={mode} />
          </div>
        )}
      </div>
    )
  }

  function renderFieldGroup(label: string, groupFields: FieldDef[]) {
    return (
      <div>
        {hasGroups && (
          <div className="border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-2">
            <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">{label}</span>
          </div>
        )}
        <div className="divide-y-[0.5px] divide-[var(--rule)]">
          {groupFields.map(renderFieldRow)}
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" bare>
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Configure Fields - {segmentLabel}</DialogTitle>
          <DialogDescription>
            Click a field to edit its label, type and visibility.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto border-y-[0.5px] border-y-[var(--rule)]">
          {hasGroups ? (
            <>
              {customerFields.length > 0 && renderFieldGroup('Customer', customerFields)}
              {transactionFields.length > 0 && (
                <div className="border-t-[0.5px] border-t-[var(--rule)]">
                  {renderFieldGroup('Transaction', transactionFields)}
                </div>
              )}
            </>
          ) : (
            <div className="divide-y-[0.5px] divide-[var(--rule)]">
              {fields.map(renderFieldRow)}
            </div>
          )}
        </div>

        {transactionFields.length > 0 && (
          <div className="mx-6 my-3 flex gap-2.5 rounded-md border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-3">
            <Info size={13} className="mt-0.5 shrink-0 text-[var(--ink-3)]" />
            <p className="m-0 text-[12px] leading-[1.55] text-[var(--ink-2)]">
              <span className="font-medium text-[var(--ink)]">Visibility modes: </span>
              <span className="font-medium">Excluded</span> completely hides the field from the registration wizard.
              Contractors won't see it at all.{' '}
              <span className="font-medium">Optional</span> shows the field but doesn't require it.{' '}
              To remove Purchase Price entirely from the wizard, set it to{' '}
              <span className="font-medium">Excluded</span> above and save.
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 px-6 py-4">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
